// client/src/pages/investments/Pea.js

import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FaFileUpload, FaPlus, FaTrash, FaPen, FaInfoCircle } from 'react-icons/fa';
import usePea from '../../hooks/usePea';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const signedEur = (v) => `${Number(v) >= 0 ? '+' : ''}${eur(v)}`;
const pct = (v) => (v == null ? '—' : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(2)} %`);
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const EMPTY_FORM = { name: '', broker: 'Boursobank', cashBalance: '' };

// Formulaire de création / édition d'un compte PEA.
const AccountModal = ({ show, initial, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (show) {
      setForm(
        initial
          ? {
              name: initial.name,
              broker: initial.broker,
              cashBalance: String(initial.cashBalance ?? '')
            }
          : EMPTY_FORM
      );
    }
  }, [show, initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name: form.name.trim(),
      broker: form.broker.trim() || 'Boursobank',
      cashBalance: form.cashBalance === '' ? 0 : Number(String(form.cashBalance).replace(',', '.'))
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? 'Modifier le compte' : 'Nouveau compte PEA'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nom</Form.Label>
            <Form.Control
              required
              value={form.name}
              placeholder="PEA Boursobank"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Courtier</Form.Label>
            <Form.Control
              value={form.broker}
              onChange={(e) => setForm({ ...form, broker: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Liquidités (€)</Form.Label>
            <Form.Control
              value={form.cashBalance}
              placeholder="0"
              onChange={(e) => setForm({ ...form, cashBalance: e.target.value })}
            />
            <Form.Text className="text-muted">
              Espèces non investies : l&apos;export de portefeuille ne les contient pas.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={saving || !form.name.trim()}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Tableau des lignes détenues d'un compte.
const PositionsTable = ({ positions, onDelete }) => {
  if (positions.length === 0) {
    return (
      <p className="text-muted mb-0">
        Aucune position : importez l&apos;export de portefeuille de votre courtier.
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <Table hover className="align-middle mb-0">
        <thead>
          <tr className="text-muted" style={{ fontSize: 12 }}>
            <th>Support</th>
            <th className="text-end">Qté</th>
            <th className="text-end">PRU</th>
            <th className="text-end">Cours</th>
            <th className="text-end">Valorisation</th>
            <th className="text-end">Plus-value</th>
            <th className="text-end">Jour</th>
            <th className="text-end">Dernier mvt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr key={p.id}>
              <td>
                <div className="fw-semibold">{p.name}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {p.isin}
                </div>
              </td>
              <td className="text-end">{Number(p.quantity).toLocaleString('fr-FR')}</td>
              <td className="text-end">{eur(p.buyingPrice)}</td>
              <td className="text-end">{eur(p.lastPrice)}</td>
              <td className="text-end fw-semibold">{eur(p.amount)}</td>
              <td className={`text-end ${p.amountVariation >= 0 ? 'text-success' : 'text-danger'}`}>
                {signedEur(p.amountVariation)}
                <div style={{ fontSize: 11 }}>{pct(p.variation)}</div>
              </td>
              <td
                className={`text-end ${
                  (p.intradayVariation || 0) >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {pct(p.intradayVariation)}
              </td>
              <td className="text-end text-muted" style={{ fontSize: 12 }}>
                {shortDate(p.lastMovementDate)}
              </td>
              <td className="text-end">
                <Button
                  size="sm"
                  variant="outline-danger"
                  title="Supprimer la ligne"
                  onClick={() => onDelete(p)}
                >
                  <FaTrash size={11} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

function Pea() {
  const {
    accounts,
    loading,
    error,
    setError,
    fetchAccounts,
    addAccount,
    editAccount,
    removeAccount,
    removePosition,
    importPortfolio
  } = usePea();

  const [modalAccount, setModalAccount] = useState(null); // compte en cours d'édition
  const [showModal, setShowModal] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputs = useRef({});

  useEffect(() => {
    fetchAccounts().catch(() => {
      // erreur déjà remontée par le hook
    });
  }, [fetchAccounts]);

  const refresh = () =>
    fetchAccounts().catch(() => {
      // erreur déjà remontée par le hook
    });

  const handleSubmit = async (data) => {
    try {
      if (modalAccount) await editAccount(modalAccount.id, data);
      else await addAccount(data);
      setShowModal(false);
      setModalAccount(null);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleDeleteAccount = async (account) => {
    if (
      !window.confirm(
        `Supprimer « ${account.name} » ? Ses positions et son historique seront perdus.`
      )
    ) {
      return;
    }
    try {
      await removeAccount(account.id);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleDeletePosition = async (position) => {
    if (!window.confirm(`Supprimer la ligne « ${position.name} » ?`)) return;
    try {
      await removePosition(position.id);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  const handleFile = async (account, event) => {
    const file = event.target.files[0];
    event.target.value = ''; // permet de réimporter le même fichier
    if (!file) return;
    setImportSummary(null);
    try {
      const summary = await importPortfolio(account.id, file);
      setImportSummary(summary);
      await refresh();
    } catch {
      // le message d'erreur est déjà affiché
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">PEA</h2>
        <Button
          variant="primary"
          onClick={() => {
            setModalAccount(null);
            setShowModal(true);
          }}
        >
          <FaPlus size={12} className="me-2" />
          Nouveau compte
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {importSummary && (
        <Alert variant="success" dismissible onClose={() => setImportSummary(null)}>
          <strong>{importSummary.accountName}</strong> — {importSummary.imported} ligne(s)
          importée(s) : {importSummary.created} ajoutée(s), {importSummary.updated} mise(s) à jour,{' '}
          {importSummary.closed} soldée(s). Valorisation {eur(importSummary.totalValue)}.
          {importSummary.skipped.length > 0 && (
            <div className="mt-1" style={{ fontSize: 13 }}>
              {importSummary.skipped.length} ligne(s) ignorée(s) :{' '}
              {importSummary.skipped.map((s) => `ligne ${s.line} (${s.reason})`).join(', ')}.
            </div>
          )}
        </Alert>
      )}

      <Alert variant="light" className="border d-flex align-items-start gap-2">
        <FaInfoCircle className="text-primary mt-1" />
        <div style={{ fontSize: 13 }}>
          L&apos;export de portefeuille Boursobank est une <strong>photo</strong> du compte
          (quantité, prix de revient, dernier cours). Chaque import remplace les positions : une
          ligne absente du fichier est considérée comme soldée, et une photo de valorisation est
          enregistrée pour alimenter la courbe d&apos;évolution.
        </div>
      </Alert>

      {loading.accounts && accounts.length === 0 ? (
        <Spinner animation="border" />
      ) : accounts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-5 text-center">
            <h5>Aucun compte PEA</h5>
            <p className="text-muted mb-0">
              Créez un compte pour commencer à importer votre portefeuille.
            </p>
          </Card.Body>
        </Card>
      ) : (
        accounts.map((account) => (
          <Card key={account.id} className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <Row className="align-items-center g-3 mb-4">
                <Col md>
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">{account.name}</h5>
                    <Badge bg="light" text="dark">
                      {account.broker}
                    </Badge>
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Dernier import : {shortDate(account.lastImportAt)} · Liquidités{' '}
                    {eur(account.cashBalance)}
                  </div>
                </Col>
                <Col md="auto" className="text-md-end">
                  <div className="fw-bold" style={{ fontSize: 22 }}>
                    {eur(account.totalWithCash)}
                  </div>
                  <div
                    className={account.gain >= 0 ? 'text-success' : 'text-danger'}
                    style={{ fontSize: 13 }}
                  >
                    {signedEur(account.gain)} ({pct(account.gainPct)})
                  </div>
                </Col>
                <Col md="auto" className="d-flex gap-2">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="d-none"
                    ref={(el) => {
                      fileInputs.current[account.id] = el;
                    }}
                    onChange={(e) => handleFile(account, e)}
                  />
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={loading.importing}
                    onClick={() => fileInputs.current[account.id].click()}
                  >
                    <FaFileUpload size={12} className="me-2" />
                    {loading.importing ? 'Import…' : 'Importer le CSV'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    title="Modifier"
                    onClick={() => {
                      setModalAccount(account);
                      setShowModal(true);
                    }}
                  >
                    <FaPen size={12} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    title="Supprimer le compte"
                    onClick={() => handleDeleteAccount(account)}
                  >
                    <FaTrash size={12} />
                  </Button>
                </Col>
              </Row>

              <PositionsTable positions={account.positions} onDelete={handleDeletePosition} />
            </Card.Body>
          </Card>
        ))
      )}

      <AccountModal
        show={showModal}
        initial={modalAccount}
        saving={loading.saving}
        onClose={() => {
          setShowModal(false);
          setModalAccount(null);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default Pea;
